import type { TourKey } from '@osteopro/shared';
import { availableCoachMarkSteps, CoachMark, type CoachMarkStep } from '@osteopro/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearch } from 'wouter';
import { useMarkTourSeen, useOnboardingStatus } from '../../hooks/api/use-onboarding.js';
import { claimAutoTour } from '../../onboarding/session-memory.js';
import {
  acknowledgeTourRequest,
  tourForPath,
  useTourRequest,
} from '../../onboarding/tour-requests.js';
import { TOURS } from '../../onboarding/tours.js';
import { useOnboardingScope } from '../../onboarding/use-onboarding-scope.js';

/** Cadence de la recherche des cibles, et délai au-delà duquel l'écran est tenu pour sans visite. */
const POLL_MS = 250;
const GIVE_UP_MS = 8000;

interface ActiveTour {
  tour: TourKey;
  steps: CoachMarkStep[];
  /** Change à chaque lancement : une visite rejouée repart du premier repère. */
  run: number;
}

/**
 * Hôte unique des visites guidées, monté dans la coque. Il lance la visite de l'écran courant :
 * - d'elle-même, à la première ouverture de l'écran par ce membre (`toursSeen` ne la contient pas),
 *   une fois par session, jamais sur un écran ouvert pour une saisie (`?new=1`) ;
 * - sur demande de l'Aide (`requestTour`), qu'elle ait déjà été vue ou non.
 *
 * La visite attend que l'écran ait fini de charger : elle démarre quand l'ensemble des cibles
 * présentes cesse de grandir. Terminée ou passée, elle est marquée vue côté serveur.
 */
export function TourHost() {
  const { t } = useTranslation();
  const [location] = useLocation();
  const search = useSearch();
  const inScope = useOnboardingScope();
  const status = useOnboardingStatus(inScope);
  const markSeen = useMarkTourSeen();
  const requested = useTourRequest();
  const [active, setActive] = useState<ActiveTour | null>(null);

  const tour = inScope ? tourForPath(location) : null;
  const shown = active !== null && active.tour === tour ? active : null;
  const seen = status.data?.toursSeen;

  const manual = requested !== null && requested === tour;
  const automatic =
    !manual &&
    tour !== null &&
    seen !== undefined &&
    !seen.includes(tour) &&
    !search.includes('new=1');
  const candidate = shown === null && (manual || automatic) ? tour : null;

  useEffect(() => {
    if (candidate === null) return;
    const definitions = TOURS[candidate];
    let previousCount = -1;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const present = availableCoachMarkSteps(definitions);
      const settled = present.length > 0 && present.length === previousCount;
      previousCount = present.length;
      if (!settled) {
        if (Date.now() - startedAt > GIVE_UP_MS) {
          clearInterval(timer);
          if (manual) acknowledgeTourRequest();
        }
        return;
      }
      clearInterval(timer);
      if (manual) acknowledgeTourRequest();
      else if (!claimAutoTour(candidate)) return;
      setActive((current) => ({
        tour: candidate,
        run: (current?.run ?? 0) + 1,
        steps: present.map((step) => ({
          target: step.target,
          title: t(`onboarding:tour.${candidate}.${step.key}.title`),
          body: t(`onboarding:tour.${candidate}.${step.key}.body`),
          ...(step.side === undefined ? {} : { side: step.side }),
        })),
      }));
    }, POLL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [candidate, manual, t]);

  if (shown === null) return null;

  const close = () => {
    markSeen.mutate(shown.tour);
    setActive(null);
  };

  return (
    <CoachMark
      key={`${shown.tour}-${String(shown.run)}`}
      steps={shown.steps}
      labels={{
        previous: t('onboarding:tour.previous'),
        next: t('onboarding:tour.next'),
        finish: t('onboarding:tour.finish'),
        skip: t('onboarding:tour.skip'),
        counter: (current, total) => t('onboarding:tour.counter', { current, total }),
      }}
      onFinish={close}
      onSkip={close}
      testIdPrefix="tour"
    />
  );
}
